/**
 * Remark plugin that rewrites `[[Title]]` / `[[Title|label]]` text in
 * markdown AST into `link` nodes pointing at `/files/<target>`.
 *
 * We emit a regular relative URL (no custom scheme) so streamdown's
 * `rehype-sanitize` + `rehype-harden` pipeline leaves the href intact.
 * The `AgentMarkdown` `<a>` override picks up the `/files/` prefix and
 * renders a TanStack `<Link>` for SPA navigation; the workspace files
 * map is consulted there for graceful target resolution.
 *
 * Doing this at the AST layer (rather than as a string preprocess on the
 * raw markdown) means we never touch text inside inline code or fenced
 * code blocks — those parse as `inlineCode` / `code` leaf nodes whose
 * content lives in `value`, not in `text` children, so the visitor
 * naturally skips them. That preserves agent responses that show wiki
 * syntax as examples, e.g. `` `[[Foo]]` `` or fenced blocks.
 */
import type { Link, Root, Text } from "mdast";
import type { Plugin } from "unified";
import { SKIP, visit } from "unist-util-visit";

import { buildFileHref } from "./file-href";

const WIKI_RE = /\[\[([^\]\n|]+)(?:\|([^\]\n]+))?\]\]/g;

export const remarkWikiLinks: Plugin<[], Root> = () => (tree) => {
  visit(tree, "text", (node, index, parent) => {
    if (parent === undefined || index === undefined) return;
    // Don't nest a link inside a link — if the agent wrote a literal
    // `[[Foo]]` inside the label of another markdown link, leave it.
    if (parent.type === "link") return;
    const replacements = splitWikiText(node);
    if (replacements === null) return;
    parent.children.splice(index, 1, ...replacements);
    return [SKIP, index + replacements.length];
  });
};

function splitWikiText(node: Text): (Text | Link)[] | null {
  const text = node.value;
  WIKI_RE.lastIndex = 0;
  const out: (Text | Link)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = WIKI_RE.exec(text)) !== null) {
    const rawTarget = match[1]?.trim();
    if (!rawTarget) continue;
    const label = (match[2] ?? rawTarget).trim();
    if (match.index > lastIndex) {
      out.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    out.push({
      type: "link",
      url: buildFileHref(rawTarget),
      children: [{ type: "text", value: label }],
    });
    lastIndex = match.index + match[0].length;
  }
  if (out.length === 0) return null;
  if (lastIndex < text.length) {
    out.push({ type: "text", value: text.slice(lastIndex) });
  }
  return out;
}
